const APP_SCHEME = 'Sportyfinder';
const ANDROID_PACKAGE_NAME = 'com.sportyfinder';

export const IOS_APP_STORE_URL = 'https://apps.apple.com/ru/app/sportyfinder/id6636485156?l=en-GB';
export const ANDROID_PLAY_STORE_URL =
  'https://play.google.com/store/apps/details?id=com.sportyfinder&pcampaignid=web_share';

/**
 * Параметр внутри `referrer` ссылки на Google Play. После установки приложение
 * читает его через Install Referrer API и открывает приглашение, по которому
 * его поставили (`src/features/navigation/deferred-deep-link.ts` в sportyfinder-app).
 */
export const INSTALL_REFERRER_LINK_PARAM = 'sf_link';

function normalizeAppPath(path: string) {
  const trimmedPath = path.trim();

  if (!trimmedPath || trimmedPath === '/') {
    return '';
  }

  return trimmedPath.startsWith('/') ? trimmedPath : `/${trimmedPath}`;
}

function isAndroidUserAgent(userAgent: string) {
  return /Android/i.test(userAgent);
}

export function isIosUserAgent(userAgent: string) {
  return /iPhone|iPad|iPod/i.test(userAgent);
}

export function createAppDeepLink(path: string) {
  return `${APP_SCHEME}://${normalizeAppPath(path)}`;
}

export function createAndroidIntentLink(path: string, fallbackUrl: string) {
  const encodedFallbackUrl = encodeURIComponent(fallbackUrl);

  return `intent://${normalizeAppPath(
    path,
  )}#Intent;scheme=${APP_SCHEME};package=${ANDROID_PACKAGE_NAME};S.browser_fallback_url=${encodedFallbackUrl};end`;
}

export function createMobileAppLaunchLink(path: string, userAgent: string, fallbackUrl: string) {
  if (isAndroidUserAgent(userAgent)) {
    return createAndroidIntentLink(path, fallbackUrl);
  }

  return createAppDeepLink(path);
}

/**
 * Ссылка на Google Play с путём приглашения в `referrer`: Play отдаёт его
 * приложению после установки, и человек попадает в приглашение, а не на
 * главный экран. Значение кодируется отдельно от самого `referrer`, потому что
 * Play раскодирует строку один раз.
 */
export function createPlayStoreUrl(path: string) {
  const appPath = normalizeAppPath(path);
  if (!appPath) {
    return ANDROID_PLAY_STORE_URL;
  }

  const referrer = `${INSTALL_REFERRER_LINK_PARAM}=${encodeURIComponent(appPath)}`;

  return `${ANDROID_PLAY_STORE_URL}&referrer=${encodeURIComponent(referrer)}`;
}

/** Магазин для платформы: App Store как есть (referrer у него нет), Google Play — с путём. */
export function createStoreUrl(path: string, userAgent: string) {
  return isIosUserAgent(userAgent) ? IOS_APP_STORE_URL : createPlayStoreUrl(path);
}

/**
 * Запоминает ссылку приглашения для iOS: у App Store нет referrer, поэтому по
 * нажатию «Скачать приложение» ссылка кладётся в буфер обмена, а приложение
 * читает её при первом запуске. Только из обработчика клика — без жеста
 * пользователя Safari в буфер писать не даёт. На Android путь уже в `referrer`,
 * буфер не трогаем.
 */
export async function copyInstallLink(url: string): Promise<boolean> {
  if (typeof navigator === 'undefined' || !isIosUserAgent(navigator.userAgent)) {
    return false;
  }
  const { clipboard } = navigator;
  if (!clipboard || typeof clipboard.writeText !== 'function') {
    return false;
  }

  try {
    await clipboard.writeText(url);
    return true;
  } catch {
    return false;
  }
}

/** Для обработчика клика: результат копирования странице не нужен, промис не утекает. */
export function rememberInstallLink(url: string): void {
  copyInstallLink(url).catch(() => undefined);
}
